import type { HandleChangeAnswerType } from '~/components/Interaction/Form/Question/model';
import type { AnswerUserType } from '~/model/AnswerUserType';
import type { UriType } from '~/model/GlobalTypes';
import type { QuestionChoiceType } from '~/model/QuestionChoiceType';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useState } from 'react';
import { keyboardKeys } from '~/const/keyCodes';
import { useFocus } from '~/hooks/useFocus';
import { ellipsis } from '~/utils/utils';
import { checkIfHasPictureChoice } from '../../utils';
import { ChoicePicture } from './ChoicePicture';

export const RadioTypeQuestion = ({
  answerUser,
  answerChoices,
  questionId,
  handleChangeAnswer,
  type,
  openGallery,
  langValue,
  disabled = false,
}: RadioTypeQuestionProps) => {
  const [inputRef, setInputFocus] = useFocus();
  const [hasFocused, setHasFocused] = useState(false);
  const hasPictureChoice = checkIfHasPictureChoice(answerChoices);

  useEffect(() => setHasFocused(false), [questionId]);

  useLayoutEffect(() => {
    if (!hasFocused && !disabled && !!inputRef.current) {
      setInputFocus();
      setHasFocused(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!inputRef.current, disabled]);

  return (
    <>
      {answerChoices.map((choice: QuestionChoiceType, index: number) => {
        const choiceUri = choice['@id'];
        const choiceId = choice.id;
        const isChecked
          = !!(answerUser?.[questionId]?.answer_choices as UriType[])?.length
            && (answerUser[questionId].answer_choices as UriType[]).every(
              uri => uri === choiceUri,
            );

        const shouldHaveRef = index === 0;
        const label = hasPictureChoice
          ? ellipsis(choice.title, 50)
          : choice.title;

        return (
          <div className="form-group radio" key={`${questionId}-${choiceId}`}>
            <div
              className={classNames('control-radio', {
                'control-radio--selected': isChecked,
                form__choice: !hasPictureChoice,
              })}
            >
              {hasPictureChoice && (
                <ChoicePicture
                  choice={choice}
                  openGallery={openGallery}
                  isChecked={isChecked}
                  handleClick={() => handleChangeAnswer(
                    choiceUri,
                    questionId,
                    type,
                    undefined,
                    false,
                  )}
                />
              )}
              <div
                className={classNames('control-legend', {
                  'control-legend--selected': isChecked,
                })}
              >
                <input
                  aria-labelledby={`label-choice-${choiceId}`}
                  className={classNames('control-input', {
                    'vtmn-radio-button': !hasPictureChoice,
                  })}
                  data-testid="radio"
                  data-cy="radio"
                  type="radio"
                  id={`choice-${choiceId}`}
                  name={`q-${questionId}`}
                  value={choiceUri}
                  checked={isChecked}
                  onChange={() => {
                    handleChangeAnswer(
                      choiceUri,
                      questionId,
                      type,
                      undefined,
                      false,
                    );
                  }}
                  disabled={disabled}
                />
                <label
                  className="form__label"
                  id={`label-choice-${choiceId}`}
                  data-testid="label"
                  htmlFor={`choice-${choiceId}`}
                  ref={shouldHaveRef ? inputRef : null}
                  onClick={() => handleChangeAnswer(
                    choiceUri,
                    questionId,
                    type,
                    undefined,
                    false,
                  )}
                  onKeyDown={e => e.key === keyboardKeys.ENTER && handleChangeAnswer(
                    choiceUri,
                    questionId,
                    type,
                    undefined,
                    false,
                  )}
                >
                  <span lang={langValue}>{label}</span>
                </label>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export interface RadioTypeQuestionProps {
  answerUser: AnswerUserType;
  answerChoices: QuestionChoiceType[];
  questionId: string;
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  openGallery: (url: string) => void;
  langValue: string;
  disabled?: boolean;
}
